import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Plus, DollarSign, Mail, UserPlus, Send, FileText, Upload, Unlock, Briefcase, Pencil, Trash2, BarChart3, Eye, ArrowRightLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import InputMonto from '../../components/InputMonto';
import ModalTrabajo from './ModalTrabajo';
import SelectorAviso from '../../components/SelectorAviso';
import SelectorCliente from '../../components/SelectorCliente';
import { ESTADO_PROYECTO } from './Proyecto';
import { money, moneyK, fecha, fechaCorta, fechaHora, hoy, Badge, Empty, ESTADO_COBRO, ESTADO_RENOVACION } from './ui';
import Texto from '../../utils/markdown';
import Vencimiento from '../../components/Vencimiento';
import FechaPago from '../../components/FechaPago';

/* El semáforo del último aumento. Salió de la pantalla de Abonos, donde ocupaba
   una columna entera sin merecerla; acá, en la ficha del cliente, es donde de
   verdad se mira antes de hablar de plata con alguien. */
const ajustePrecio = (fechaAjuste) => {
  if (!fechaAjuste) return { texto: 'nunca se ajustó', color: '#ef4444' };
  const meses = Math.round((Date.now() - new Date(fechaAjuste)) / 2592000000);
  const texto = meses < 1 ? 'ajustado este mes' : `ajustado hace ${meses} mes${meses === 1 ? '' : 'es'}`;
  return { texto, color: meses >= 6 ? '#ef4444' : meses >= 3 ? '#f59e0b' : '#22c55e' };
};

const PLANTILLA_MINUTA = 'Resumen de la reunión:\n\n\nTemas tratados:\n· \n\nDecisiones tomadas:\n· \n\nPróximos pasos:\n· ';
const TIPO_CONTRATO = { abono: 'Abono mensual', anual: 'Renovación anual', proyecto: 'Proyecto', unico: 'Pago único' };

export default function AgenciaClienteDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [form, setForm] = useState({});
  const [listas, setListas] = useState({ servicios: [], medios: [], agencias: [] });
  const [saving, setSaving] = useState(false);
  const [cobroNuevo, setCobroNuevo] = useState(null);
  const [contratoNuevo, setContratoNuevo] = useState(null);
  const [pago, setPago] = useState(null);
  const [contactoNuevo, setContactoNuevo] = useState(null);
  const [factura, setFactura] = useState(null);
  const [trabajo, setTrabajo] = useState(false);
  const [minutas, setMinutas] = useState([]);
  const [minuta, setMinuta] = useState(null);
  const [notas, setNotas] = useState([]);
  const [nota, setNota] = useState(null);
  /* Va acá arriba y no al lado de borrarProyecto, que es donde se usa: más abajo
     hay un `return` mientras carga, y un hook después de ese return hace que en
     el primer render se ejecuten menos hooks que en el segundo. React corta la
     pantalla entera con el error #310. */
  const [aBorrar, setABorrar] = useState(null);
  const [accesos, setAccesos] = useState(null);   // historial de ingresos al portal
  const [aMudar, setAMudar] = useState(null);     // proyecto que se pasa a otro cliente
  const [clientes, setClientes] = useState([]);

  const load = useCallback(() => {
    api.get('/agency/clientes/' + id).then(r => { setD(r.data); setForm(r.data.cliente); }).catch(() => toast.error('No se pudo cargar'));
    api.get(`/agency/clientes/${id}/minutas`).then(r => setMinutas(r.data)).catch(() => {});
    api.get(`/agency/clientes/${id}/notas`).then(r => setNotas(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); api.get('/agency/listas').then(r => setListas(r.data)).catch(() => {}); }, [load]);
  useEffect(() => { api.get('/agency/clientes').then(r => setClientes(r.data)).catch(() => {}); }, []);
  if (!d) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Cargando...</div>;

  const guardar = async () => {
    setSaving(true);
    try { await api.put('/agency/clientes/' + id, form); toast.success('Guardado'); load(); }
    catch { toast.error('Error al guardar'); } finally { setSaving(false); }
  };
  // el estado de un cobro se puede cambiar siempre, incluso volver atrás un pago
  const cambiarEstadoServicio = async (c, estado) => {
    if (estado === c.estado) return;
    try {
      await api.put('/agency/contratos/' + c.id, { estado });
      toast.success(estado === 'activo' ? 'Servicio reactivado' : estado === 'pausado' ? 'Servicio en pausa' : 'Servicio dado de baja');
      load();
    } catch { toast.error('Error'); }
  };
  const borrarServicio = async (c) => {
    if (!window.confirm(`¿Borrar "${c.nombre || 'el servicio'}"?\n\nSi solo dejó de estar vigente, conviene pausarlo o darlo de baja: así se conserva el historial de cobros.`)) return;
    try { await api.delete('/agency/contratos/' + c.id); toast.success('Servicio borrado'); load(); }
    catch { toast.error('No se pudo borrar'); }
  };
  const subirFoto = async (c, archivo) => {
    if (!archivo) return;
    const fd = new FormData(); fd.append('foto', archivo);
    try {
      await api.post(`/agency/contactos/${c.id}/foto`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Foto actualizada');
      load();
    } catch { toast.error('No se pudo subir la foto'); }
  };

  const verAccesos = async (c) => {
    setAccesos({ usuario: c, cargando: true, accesos: [] });
    try {
      const { data } = await api.get(`/agency/contactos/${c.id}/accesos?dias=30`);
      setAccesos({ usuario: c, cargando: false, ...data });
    } catch { toast.error('No se pudo cargar el historial'); setAccesos(null); }
  };

  // Ver el portal como lo ve el cliente (media hora, solo lectura)
  const verPortal = async () => {
    try {
      const { data } = await api.get(`/agency/clientes/${id}/ver-portal`);
      window.open(data.url, '_blank', 'noopener');
    } catch { toast.error('No se pudo abrir el portal'); }
  };

  const borrarProyecto = async (p) => {
    try {
      const { data } = await api.get(`/agency/proyectos/${p.id}/dependencias`);
      setABorrar(data);
    } catch { toast.error('No se pudo consultar el proyecto'); }
  };
  /* Pasar un proyecto a otro cliente. El backend le muda también los cobros y
     los servicios: si no, el trabajo queda en un cliente y la plata en otro. */
  const mudarProyecto = async () => {
    try {
      const { data } = await api.put('/agency/proyectos/' + aMudar.proyecto.id, { cliente_id: aMudar.cliente_id });
      const m = data.mudanza;
      const arrastra = m ? [m.cobros ? `${m.cobros} cobro${m.cobros === 1 ? '' : 's'}` : '', m.servicios ? `${m.servicios} servicio${m.servicios === 1 ? '' : 's'}` : ''].filter(Boolean).join(' y ') : '';
      toast.success(m ? `"${aMudar.proyecto.nombre}" pasó a ${m.a}${arrastra ? `, con ${arrastra}` : ''}` : 'Guardado', { duration: 7000 });
      setAMudar(null); load();
    } catch { toast.error('No se pudo mover el proyecto'); }
  };

  const confirmarBorrado = async (queHacer) => {
    try {
      const { data } = await api.delete(`/agency/proyectos/${aBorrar.proyecto.id}?cobros=${queHacer}`);
      const partes = ['Proyecto borrado'];
      if (data.cobros_borrados) partes.push(`${data.cobros_borrados} cobro${data.cobros_borrados === 1 ? '' : 's'} borrado${data.cobros_borrados === 1 ? '' : 's'}`);
      if (data.cobros_conservados?.length) partes.push(`${data.cobros_conservados.length} con pagos se conservaron`);
      toast.success(partes.join(' · '));
      setABorrar(null); load();
    } catch { toast.error('No se pudo borrar'); }
  };
  const habilitarCobro = async (c) => {
    const avisar = window.confirm(`Se habilita "${c.concepto}" de ${money(c.monto)} y pasa a ser cobrable.\n\n¿Le avisamos al cliente por mail?\n\nAceptar: se habilita y le avisamos.\nCancelar: se habilita sin avisarle.`);
    try {
      const { data } = await api.post(`/agency/cobros/${c.id}/habilitar`, { avisar });
      toast.success(data.aviso ? 'Habilitado · le avisamos a ' + data.aviso.join(', ') : 'Habilitado');
      load();
    } catch { toast.error('Error'); }
  };
  const cambiarEstadoCobro = async (c, estado) => {
    if (estado === c.estado) return;
    if (estado === 'pagado') return setPago({ id: c.id, fecha: hoy(), monto: c.monto });
    try {
      const { data } = await api.put('/agency/cobros/' + c.id, { estado });
      toast.success(data.revertido ? 'Se quitó el pago registrado' : 'Estado actualizado');
      load();
    } catch { toast.error('Error'); }
  };
  const guardarNota = async (e) => {
    e.preventDefault();
    try {
      if (nota.id) await api.put('/agency/movimientos/' + nota.id, { titulo: nota.titulo, detalle: nota.detalle, fecha: nota.fecha });
      else await api.post('/agency/movimientos', { ...nota, entidad: 'cliente', entidad_id: id, tipo: 'nota', visible_cliente: 0, notificar: 'no' });
      toast.success('Nota guardada'); setNota(null); load();
    } catch (err) { toast.error(err.response?.data?.error || 'No se pudo guardar'); }
  };
  const borrarNota = async (n) => {
    if (!window.confirm('¿Borrar esta nota?')) return;
    try { await api.delete('/agency/movimientos/' + n.id); load(); } catch { toast.error('Error'); }
  };
  const guardarMinuta = async (e) => {
    e.preventDefault();
    try {
      if (minuta.id) await api.put('/agency/movimientos/' + minuta.id, minuta);
      else {
        const { data } = await api.post('/agency/movimientos', { ...minuta, entidad: 'cliente', entidad_id: id, tipo: 'minuta' });
        if (data.aviso && data.aviso.error) toast.error(data.aviso.error);
      }
      toast.success('Minuta guardada'); setMinuta(null); load();
    } catch (err) { toast.error(err.response?.data?.error || 'No se pudo guardar'); }
  };
  const borrarMinuta = async (m) => {
    if (!window.confirm('¿Borrar esta minuta?')) return;
    try { await api.delete('/agency/movimientos/' + m.id); load(); } catch { toast.error('Error'); }
  };

  const crearCobro = async (e) => {
    e.preventDefault();
    try { await api.post('/agency/cobros', { ...cobroNuevo, cliente_id: id }); toast.success('Cobro creado'); setCobroNuevo(null); load(); }
    catch { toast.error('Error'); }
  };
  const crearContrato = async (e) => {
    e.preventDefault();
    try {
      if (contratoNuevo.id) await api.put('/agency/contratos/' + contratoNuevo.id, contratoNuevo);
      else await api.post('/agency/contratos', { ...contratoNuevo, cliente_id: id });
      toast.success(contratoNuevo.id ? 'Contrato actualizado' : 'Contrato creado'); setContratoNuevo(null); load();
    }
    catch { toast.error('Error'); }
  };
  const crearContacto = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/agency/clientes/${id}/contactos`, contactoNuevo);
      if (data.avisado) toast.success('Usuario creado · le mandamos la bienvenida a ' + data.avisado);
      else if (data.error) toast.error('Usuario creado, pero el mail no salió: ' + data.error, { duration: 8000 });
      else toast.success('Usuario creado sin avisarle · mandale la bienvenida con el botón ✉', { duration: 7000 });
      setContactoNuevo(null); load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };
  const invitar = async (c) => {
    try { await api.post(`/agency/contactos/${c.id}/invitar`); toast.success('Acceso enviado a ' + c.email); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'No se pudo enviar', { duration: 7000 }); }
  };
  const cambiarContacto = async (c, cambios) => {
    try { await api.put('/agency/contactos/' + c.id, cambios); load(); } catch { toast.error('Error'); }
  };
  const guardarFactura = async (e) => {
    e.preventDefault();
    try {
      const fd = new FormData();
      ['numero', 'tipo', 'fecha', 'monto'].forEach(k => factura[k] && fd.append(k, factura[k]));
      fd.append('enviar', factura.enviar ? 'true' : 'false');
      if (factura.archivo) fd.append('archivo', factura.archivo);
      await api.post(`/agency/cobros/${factura.cobro.id}/factura`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(factura.enviar ? 'Factura guardada y enviada' : 'Factura guardada');
      setFactura(null); load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };
  const registrarPago = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/agency/cobros/${pago.id}/pagos`, pago);
      toast.success(data.aviso ? `Pago registrado · le avisamos a ${data.aviso.join(', ')}` : 'Pago registrado');
      setPago(null); load();
    }
    catch { toast.error('Error'); }
  };

  const totalFacturado = d.por_anio.reduce((a, x) => a + Number(x.total), 0);
  // lo del mes en curso: cuenta por la fecha de emisión y, si no la tiene, por el vencimiento
  const mesActual = hoy().slice(0, 7);
  const delMes = d.cobros.filter(x => x.estado !== 'anulado'
    && String(x.emitido || x.vencimiento || '').slice(0, 7) === mesActual);
  const facturadoMes = delMes.reduce((a, x) => a + Number(x.monto), 0);
  const cobradoMes = delMes.filter(x => x.estado === 'pagado').reduce((a, x) => a + Number(x.monto), 0);
  const pendiente = d.cobros.filter(c => c.estado !== 'pagado' && c.estado !== 'anulado').reduce((a, c) => a + Number(c.monto), 0);
  const label = { fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 };
  const th = { textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, padding: '6px 8px' };
  const td = { padding: '8px', borderTop: '1px solid var(--border)', fontSize: 13 };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const ESTADO_SERVICIO = {
    activo: { color: '#22c55e', bg: 'rgba(34,197,94,0.15)' },
    pausado: { color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
    baja: { color: '#64748b', bg: 'rgba(100,116,139,0.15)' },
  };
  const consultasNuevas = (d.consultas || []).filter(c => c.estado === 'nueva');

  // Todas las secciones se ven igual: título, bajada y acciones a la derecha
  const Seccion = ({ titulo, sub, acciones, children, estilo }) => (
    <div className="card" style={{ marginBottom: 18, ...estilo }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h3 style={{ fontSize: 16, fontWeight: 700 }}>{titulo}</h3>
        {acciones && <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{acciones}</div>}
      </div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>}
      <div style={{ marginTop: 12 }}>{children}</div>
    </div>
  );

  return (
    <div>
      <button className="btn btn-ghost btn-sm" onClick={() => navigate('/agencia/clientes')} style={{ marginBottom: 12 }}>
        <ArrowLeft size={15} /> Clientes
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4, flexWrap: 'wrap' }}>
        <label title="Subir el logo del cliente"
          style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          <img src={d.cliente.logo_url || '/icon-portal-180.png'} alt="" onError={e => { e.target.src = '/icon-portal-180.png'; }}
            style={{ width: 46, height: 46, borderRadius: 12, objectFit: 'cover', background: '#fff', border: '1px solid var(--border)' }} />
          <span style={{ fontSize: 10, color: 'var(--accent)', fontWeight: 600 }}>{d.cliente.logo_url ? 'cambiar' : 'subir logo'}</span>
          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async (ev) => {
            const f = ev.target.files[0];
            if (!f) return;
            const fd = new FormData(); fd.append('logo', f);
            try {
              await api.post(`/agency/clientes/${id}/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
              toast.success('Logo actualizado'); load();
            } catch { toast.error('No se pudo subir'); }
          }} />
        </label>
        <h1 style={{ fontSize: 24, fontWeight: 800 }}>{d.cliente.nombre}</h1>
        <Badge label={d.cliente.tipo === 'agencia' ? 'Agencia' : d.cliente.estado}
          color={d.cliente.estado === 'activo' ? '#22c55e' : 'var(--text-muted)'} />
        {d.cliente.agencia_nombre && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>traído por {d.cliente.agencia_nombre}</span>}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-primary btn-sm" onClick={() => setTrabajo(true)}><Briefcase size={15} /> Nuevo trabajo</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setCobroNuevo({ monto: '', vencimiento: hoy(), estado: 'pendiente', concepto: '' })}>
            <Plus size={15} /> Cobro
          </button>
        </div>
      </div>
      {d.cliente.nombres_anteriores && d.cliente.nombres_anteriores !== d.cliente.nombre && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 18 }}>Antes figuraba como: {d.cliente.nombres_anteriores}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, margin: '16px 0 20px' }}>
        <div className="card card-dato" style={{ padding: '12px 14px' }}>
          <div style={label}>Facturado este mes</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: facturadoMes ? 'var(--accent)' : 'var(--text-primary)' }}>{moneyK(facturadoMes)}</div>
          {facturadoMes > 0 && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{moneyK(cobradoMes)} ya cobrado</div>}
        </div>
        <div className="card card-dato" style={{ padding: '12px 14px' }}><div style={label}>Facturado histórico</div><div style={{ fontSize: 22, fontWeight: 800 }}>{moneyK(totalFacturado)}</div></div>
        <div className="card card-dato" style={{ padding: '12px 14px' }}><div style={label}>Por cobrar</div><div style={{ fontSize: 22, fontWeight: 800, color: pendiente ? '#f59e0b' : 'var(--text-primary)' }}>{moneyK(pendiente)}</div></div>
        <div className="card card-dato" style={{ padding: '12px 14px' }}><div style={label}>Servicios activos</div><div style={{ fontSize: 22, fontWeight: 800 }}>{d.contratos.filter(c => c.estado === 'activo').length}</div></div>
        <div className="card card-dato" style={{ padding: '12px 14px' }}><div style={label}>Usuarios del portal</div><div style={{ fontSize: 22, fontWeight: 800 }}>{d.contactos.filter(c => c.acceso !== 'desactivado').length}</div></div>
      </div>

      {consultasNuevas.length > 0 && (
        <Seccion titulo="Pidió que lo contactes" sub="Consultas que dejó desde su portal" estilo={{ borderLeft: '3px solid var(--accent)' }}>
          {consultasNuevas.map(c => (
            <div key={c.id} style={{ borderTop: '1px solid var(--border)', padding: '10px 0', display: 'flex', gap: 10, justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--accent)' }}>{c.servicio || 'Un servicio'}</div>
                {c.mensaje && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{c.mensaje}</div>}
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{c.contacto || 'Desde el portal'} · {fecha(c.created_at)}</div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={async () => {
                await api.put('/agency/consultas/' + c.id, { estado: 'contactada' }).catch(() => toast.error('Error'));
                load();
              }}>Ya lo contacté</button>
            </div>
          ))}
        </Seccion>
      )}

      <Seccion titulo="Cobros y pagos"
        sub={d.por_anio.length ? d.por_anio.map(a => `${a.anio}: ${money(a.total)}`).join('  ·  ') : null}
        acciones={<button className="btn btn-secondary btn-sm" onClick={() => setCobroNuevo({ monto: '', vencimiento: hoy(), estado: 'pendiente', concepto: '' })}><Plus size={14} /> Nuevo</button>}>
        {d.cobros.length ? (
          <div className="table-container">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Concepto</th><th style={th}>Emitido</th><th style={th}>Vence</th><th style={{ ...th, textAlign: 'right' }}>Monto</th><th style={th}>Estado</th><th style={th}></th></tr></thead>
              <tbody>{d.cobros.map(c => {
                const e = ESTADO_COBRO[c.estado] || {};
                return (
                  <tr key={c.id} style={{ opacity: c.habilitado ? 1 : 0.65 }}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{c.concepto || '—'}</div>
                      {c.notas && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.notas}</div>}
                      <button className="btn btn-ghost btn-sm" style={{ padding: 0, fontSize: 11, color: c.factura_numero || c.factura_url ? 'var(--accent)' : 'var(--text-muted)' }}
                        onClick={() => setFactura({
                          cobro: c,
                          numero: c.factura_numero || '',
                          tipo: c.factura_tipo || '',
                          // si ya tiene factura se abre con sus datos; si no, se propone el vencimiento
                          fecha: (c.factura_fecha || c.vencimiento || '').slice(0, 10),
                          url: c.factura_url || null,
                          enviada: c.factura_enviada || null,
                          enviar: false,
                        })}>
                        <FileText size={11} /> {c.factura_numero || c.factura_url ? 'Ver factura' : 'Cargar factura'}
                      </button>
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 12, color: 'var(--text-muted)' }}>{fechaCorta(c.emitido)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 12 }}>
                      <Vencimiento cobro={c} onCambio={load}>{fechaCorta(c.vencimiento)}</Vencimiento>
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{money(c.monto)}</td>
                    <td style={td}>
                      {/* Mientras está a pasar no muestra además "pendiente": son
                          dos momentos distintos del mismo cobro */}
                      {!c.habilitado ? (
                        <Badge label="A pasar" color="#64748b" bg="rgba(100,116,139,0.15)" />
                      ) : (
                      <select value={c.estado} onChange={ev => cambiarEstadoCobro(c, ev.target.value)} title="Cambiar estado"
                        style={{ width: 'auto', padding: '4px 26px 4px 10px', fontSize: 12, fontWeight: 700, borderRadius: 999,
                          background: e.bg || 'transparent', color: e.color, border: '1px solid var(--border)' }}>
                        {Object.entries(ESTADO_COBRO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                      </select>
                      )}
                      {c.estado === 'pagado' && (
                        <div style={{ marginTop: 3 }}><FechaPago cobro={c} editable onCambio={load} /></div>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {!c.habilitado
                        ? <button className="btn btn-ghost btn-sm" onClick={() => habilitarCobro(c)} title="Pasarlo al cliente"><Unlock size={14} /> Habilitar</button>
                        : c.estado !== 'pagado' && <button className="btn btn-ghost btn-sm" onClick={() => setPago({ id: c.id, fecha: hoy(), monto: c.monto })}><DollarSign size={14} /> Pagó</button>}
                    </td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        ) : <Empty>Sin cobros registrados</Empty>}
      </Seccion>

      {/* Los reportes, a mano: es lo que más se busca de un cliente con pauta */}
      <Seccion titulo="Reportes" sub={(d.reportes || []).length
        ? `${d.reportes.filter(r => r.publicado).length} publicado${d.reportes.filter(r => r.publicado).length === 1 ? '' : 's'} de ${d.reportes.length}`
        : 'Todavía no se cargó ninguno'}>
        {(d.reportes || []).length ? (
          <div className="reportes-grilla">
            {d.reportes.map(r => (
              <div key={r.id} className="reporte-tarjeta">
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                  <span className="reporte-icono"><BarChart3 size={16} /></span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 13.5 }}>{r.periodo}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.servicio || 'Sin servicio'}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  <Badge label={r.publicado ? 'Publicado' : 'Borrador'}
                    color={r.publicado ? '#22c55e' : 'var(--text-muted)'}
                    bg={r.publicado ? 'rgba(34,197,94,0.15)' : 'transparent'} />
                  {r.documento_url && (
                    <a className="btn btn-ghost btn-sm" href={r.documento_url} target="_blank" rel="noopener noreferrer" title="Abrir el informe">
                      <FileText size={15} />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : <Empty>Cuando subas el primero, va a aparecer acá</Empty>}
      </Seccion>

      <Seccion titulo="Proyectos y servicios"
        acciones={<>
          <button className="btn btn-secondary btn-sm" onClick={() => setTrabajo(true)}><Briefcase size={14} /> Nuevo trabajo</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setContratoNuevo({ tipo: 'abono', monto: '', dia_cobro: '' })}><Plus size={14} /> Servicio suelto</button>
        </>}>
        {d.proyectos.length > 0 && (
          <div style={{ marginBottom: d.contratos.length ? 18 : 0 }}>
            {d.proyectos.map(p => (
              <div key={p.id} style={{ borderTop: '1px solid var(--border)', padding: '11px 0', display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                <div style={{ cursor: 'pointer', minWidth: 0 }} onClick={() => navigate('/agencia/proyectos/' + p.id)}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{p.nombre}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {(ESTADO_PROYECTO[p.estado] || {}).label || p.estado}{p.fecha_inicio ? ` · desde ${fecha(p.fecha_inicio)}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {p.valor ? <span style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>{money(p.valor)}</span> : null}
                  <button className="btn btn-ghost btn-sm" title="Pasarlo a otro cliente"
                    onClick={() => setAMudar({ proyecto: p, cliente_id: '' })}><ArrowRightLeft size={14} /></button>
                  <button className="btn btn-ghost btn-sm" title="Borrar proyecto" onClick={() => borrarProyecto(p)}><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {d.contratos.length ? (
          <div className="table-container">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Servicio</th><th style={th}>Tipo</th><th style={{ ...th, textAlign: 'right' }}>Monto</th><th style={th}>Estado</th><th style={th}></th></tr></thead>
              <tbody>{d.contratos.map(c => {
                const es = ESTADO_SERVICIO[c.estado] || ESTADO_SERVICIO.baja;
                return (
                  <tr key={c.id} style={{ opacity: c.estado === 'activo' ? 1 : 0.7 }}>
                    <td style={{ ...td, cursor: 'pointer' }} onClick={() => navigate('/agencia/servicios/' + c.id)} title="Ver la ficha del servicio">
                      <div style={{ fontWeight: 600 }}>{c.nombre || c.servicio || '—'}</div>
                      {c.tipo === 'anual' && c.proximo_vencimiento && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>vence {fecha(c.proximo_vencimiento)}</div>}
                      {c.tipo === 'abono' && c.estado === 'activo' && (() => {
                        const a = ajustePrecio(c.ultima_actualizacion_precio);
                        return (
                          <div style={{ fontSize: 11, color: a.color, fontWeight: 600, marginTop: 2 }}>
                            {a.texto}
                            {c.ultima_actualizacion_precio && (
                              <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> · {fecha(c.ultima_actualizacion_precio)}</span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td style={{ ...td, fontSize: 12 }}>{TIPO_CONTRATO[c.tipo]}{c.dia_cobro ? ` · día ${c.dia_cobro}` : ''}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{money(c.monto)}</td>
                    <td style={td}>
                      <select value={c.estado} onChange={ev => cambiarEstadoServicio(c, ev.target.value)} title="Cambiar estado"
                        style={{ width: 'auto', padding: '4px 24px 4px 9px', fontSize: 12, fontWeight: 700, borderRadius: 999,
                          background: es.bg, color: es.color, border: '1px solid var(--border)' }}>
                        <option value="activo">Activo</option><option value="pausado">En pausa</option><option value="baja">Dado de baja</option>
                      </select>
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                      <button className="btn btn-ghost btn-sm" title="Editar"
                        onClick={() => setContratoNuevo({ ...c, monto: String(Math.round(Number(c.monto))), proximo_vencimiento: c.proximo_vencimiento ? String(c.proximo_vencimiento).slice(0, 10) : '', ultima_actualizacion_precio: undefined })}>
                        <Pencil size={14} /></button>
                      <button className="btn btn-ghost btn-sm" title="Borrar" onClick={() => borrarServicio(c)}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        ) : !d.proyectos.length && <Empty>Todavía no tiene proyectos ni servicios</Empty>}
      </Seccion>

      <Seccion titulo="Usuarios del portal"
        sub={`Entran en clientes.udream.app. ${d.cliente.facturar_a === 'agencia' ? 'Como se factura a la agencia, por defecto no ven importes.' : 'Ven sus facturas y pueden avisar los pagos.'}`}
        acciones={<>
          <button className="btn btn-secondary btn-sm" onClick={verPortal} title="Abre su portal en modo lectura, por media hora">
            <Eye size={14} /> Ver su portal
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => setContactoNuevo({ nombre: '', email: '', invitar: true, cobros_desde: (d.cliente.portal_desde || '').slice(0, 10) })}>
            <UserPlus size={14} /> Agregar
          </button>
        </>}>
        {d.contactos.length ? (
          <div className="table-container">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Usuario</th><th style={th}>Estado</th><th style={th}>Avisos</th><th style={th}>Última conexión</th><th style={th}></th></tr></thead>
              <tbody>{d.contactos.map(c => (
                <tr key={c.id}>
                  <td style={td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {/* La foto se ve en su portal y en los mails que le llegan */}
                      <label title="Cambiar la foto de este usuario" style={{ cursor: 'pointer', flexShrink: 0 }}>
                        {c.foto_url
                          ? <img src={c.foto_url} alt="" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', display: 'block' }} />
                          : <span style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--bg-hover)',
                              border: '1px dashed var(--border)', display: 'grid', placeItems: 'center',
                              fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' }}>
                              {(c.nombre || c.email || '?').trim()[0].toUpperCase()}
                            </span>}
                        <input type="file" accept="image/*" style={{ display: 'none' }}
                          onChange={e => subirFoto(c, e.target.files[0])} />
                      </label>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600 }}>{c.nombre || c.email}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.email}</div>
                      </div>
                    </div>
                  </td>
                  <td style={td}><Badge label={{ sin_invitar: 'Sin invitar', invitado: 'Invitado', activo: 'Activo', desactivado: 'Desactivado' }[c.acceso]}
                    color={c.acceso === 'activo' ? '#22c55e' : c.acceso === 'invitado' ? '#f59e0b' : 'var(--text-muted)'} /></td>
                  <td style={td}>
                    {c.avisa_pagos && c.avisa_novedades
                      ? <span style={{ fontSize: 12, color: '#22c55e' }}>Todos</span>
                      : (!c.avisa_pagos && !c.avisa_novedades)
                        ? <Badge label="Sin mails" color="#ef4444" bg="rgba(239,68,68,0.15)" />
                        : <Badge label={c.avisa_pagos ? 'Solo pagos' : 'Solo novedades'} color="#f59e0b" bg="rgba(245,158,11,0.15)" />}
                    {c.dispositivos > 0 && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{c.dispositivos} en el celular</div>}
                  </td>
                  <td style={{ ...td, fontSize: 12 }}>
                    {c.ultimo_acceso ? (
                      <button className="enlace-sutil" onClick={() => verAccesos(c)} title="Ver los últimos 30 días">
                        <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{fechaHora(c.ultimo_acceso)}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {c.entradas_30d > 0 ? `${c.entradas_30d} ${c.entradas_30d === 1 ? 'entrada' : 'entradas'} en 30 días` : 'sin entradas en 30 días'}
                        </div>
                      </button>
                    ) : <span style={{ color: 'var(--text-muted)' }}>Nunca entró</span>}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => invitar(c)} title={c.acceso === 'sin_invitar' ? 'Enviar el acceso por email' : 'Reenviar el acceso'}><Send size={14} /></button>
                    <button className="btn btn-ghost btn-sm" style={{ color: 'var(--text-muted)' }}
                      onClick={() => cambiarContacto(c, { acceso: c.acceso === 'desactivado' ? 'invitado' : 'desactivado' })}>
                      {c.acceso === 'desactivado' ? 'Reactivar' : 'Desactivar'}
                    </button>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        ) : <Empty>Todavía no tiene acceso al portal</Empty>}
      </Seccion>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 18 }}>
        <Seccion titulo="Minutas de reuniones" sub="El cliente las ve en su portal"
          acciones={<button className="btn btn-secondary btn-sm" onClick={() => setMinuta({ titulo: '', detalle: PLANTILLA_MINUTA, fecha: hoy(), visible_cliente: 1, notificar: 'portal' })}>
            <Plus size={14} /> Cargar</button>}>
          {minutas.length ? minutas.map(m => {
            // Las grabadas se administran en Reuniones: editarlas acá tocaría otra tabla
            const esReunion = m.origen === 'reunion';
            return (
              <details key={`${m.origen}-${m.id}`} style={{ borderTop: '1px solid var(--border)', padding: '10px 0', fontSize: 13 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span>{m.titulo}</span>
                  <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 11 }}>{fecha(m.fecha)}</span>
                  {esReunion && <Badge label="Reunión" color="#1b7fe6" bg="rgba(27,127,230,0.14)" />}
                  {!m.visible_cliente && <Badge label={esReunion ? 'Sin publicar' : 'Interna'} color="#64748b" bg="rgba(100,116,139,0.15)" />}
                </summary>
                {m.detalle && <Texto texto={m.detalle} className="md-mov" />}
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  {esReunion ? (
                    <button className="btn btn-ghost btn-sm" onClick={() => navigate('/reuniones')}>
                      <FileText size={13} /> Abrir en Reuniones
                    </button>
                  ) : (
                    <>
                      <button className="btn btn-ghost btn-sm" onClick={() => setMinuta({ ...m, fecha: (m.fecha || '').slice(0, 10) })}>Editar</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => borrarMinuta(m)}>Borrar</button>
                    </>
                  )}
                </div>
              </details>
            );
          }) : <Empty>Todavía no cargaste ninguna minuta</Empty>}
        </Seccion>

        <Seccion titulo="Notas internas" sub="Para vos: el cliente nunca las ve"
          acciones={<button className="btn btn-secondary btn-sm" onClick={() => setNota({ titulo: '', detalle: '', fecha: hoy() })}><Plus size={14} /> Nueva</button>}>
          {notas.length ? notas.map(n => (
            <details key={n.id} style={{ borderTop: '1px solid var(--border)', padding: '10px 0', fontSize: 13 }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600, display: 'flex', gap: 8, alignItems: 'center' }}>
                <span>{n.titulo}</span>
                <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 11 }}>{fecha(n.fecha)}</span>
              </summary>
              {n.detalle && <Texto texto={n.detalle} className="md-mov" />}
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setNota({ ...n, fecha: (n.fecha || '').slice(0, 10) })}>Editar</button>
                <button className="btn btn-ghost btn-sm" onClick={() => borrarNota(n)}>Borrar</button>
              </div>
            </details>
          )) : <Empty>Sin notas todavía</Empty>}
        </Seccion>
      </div>

      {(d.propuestas.length > 0 || d.subclientes.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 18 }}>
          {d.propuestas.length > 0 && (
            <Seccion titulo="Propuestas">
              {d.propuestas.map(p => (
                <div key={p.id} style={{ padding: '9px 0', borderTop: '1px solid var(--border)', fontSize: 13, display: 'flex', justifyContent: 'space-between', gap: 10, cursor: 'pointer' }}
                  onClick={() => navigate('/agencia/propuestas')}>
                  <span style={{ fontWeight: 600 }}>{p.titulo}</span>
                  <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{String(p.estado).replace('_', ' ')}</span>
                </div>
              ))}
            </Seccion>
          )}
          {d.subclientes.length > 0 && (
            <Seccion titulo="Clientes de esta agencia">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {d.subclientes.map(sc => (
                  <button key={sc.id} className="btn btn-ghost btn-sm" onClick={() => navigate('/agencia/clientes/' + sc.id)}>{sc.nombre}</button>
                ))}
              </div>
            </Seccion>
          )}
        </div>
      )}

      {/* Datos del cliente: se editan poco, van al final y a todo el ancho */}
      <details className="card">
        <summary style={{ cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>Datos del cliente y facturación</summary>
        <div className="grilla-datos" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0 16px', marginTop: 16 }}>
          <div className="form-group" style={{ gridColumn: 'span 2' }}><label>Nombre</label><input value={form.nombre || ''} onChange={set('nombre')} /></div>
          <div className="form-group"><label>Estado</label>
            <select value={form.estado || 'activo'} onChange={set('estado')}>
              <option value="activo">Activo</option><option value="inactivo">Inactivo</option><option value="prospecto">Prospecto</option>
            </select></div>
          <div className="form-group" style={{ gridColumn: 'span 2' }}><label>Razón social</label><input value={form.razon_social || ''} onChange={set('razon_social')} /></div>
          <div className="form-group"><label>CUIT</label><input value={form.cuit || ''} onChange={set('cuit')} /></div>
          <div className="form-group"><label>Condición IVA</label>
            <select value={form.condicion_iva || ''} onChange={set('condicion_iva')}>
              <option value="">—</option><option>Responsable Inscripto</option><option>Monotributo</option><option>Exento</option><option>Consumidor Final</option>
            </select></div>
          <div className="form-group" style={{ gridColumn: 'span 2' }}><label>Email de facturación</label>
            <input type="email" value={form.email_facturacion || ''} onChange={set('email_facturacion')} /></div>
          <div className="form-group"><label>Teléfono</label><input value={form.telefono || ''} onChange={set('telefono')} /></div>
          <div className="form-group" style={{ gridColumn: 'span 2' }}><label>Web</label><input value={form.web || ''} onChange={set('web')} /></div>
          <div className="form-group"><label>Traído por</label>
            <select value={form.agencia_id || ''} onChange={set('agencia_id')}>
              <option value="">Directo</option>
              {listas.agencias.filter(a => a.id !== Number(id)).map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
            </select></div>
          <div className="form-group"><label>Facturar a</label>
            <select value={form.facturar_a || 'cliente'} onChange={set('facturar_a')}>
              <option value="cliente">Al cliente</option><option value="agencia">A la agencia</option>
            </select></div>
          <div className="form-group" style={{ gridColumn: '1 / -1' }}><label>Notas internas del cliente</label>
            <textarea rows={2} value={form.notas || ''} onChange={set('notas')} style={{ fontFamily: 'inherit', fontSize: 13 }} /></div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 14, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.sugerencias === undefined ? true : !!form.sugerencias}
            onChange={e => setForm({ ...form, sugerencias: e.target.checked ? 1 : 0 })} style={{ width: 16, height: 16 }} />
          Mostrarle sugerencias de otros servicios en su portal
        </label>
        <div className="form-group" style={{ maxWidth: 320 }}>
          <label>En el portal, ve sus cobros desde</label>
          <input type="date" value={(form.portal_desde || '').slice(0, 10)}
            onChange={e => setForm({ ...form, portal_desde: e.target.value })} />
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 5 }}>
            {form.portal_desde
              ? 'En su portal solo aparecen los cobros de esta fecha en adelante. Acá seguís viendo todos.'
              : 'Vacío: ve todo su historial de cobros.'}
          </div>
        </div>
        <button className="btn btn-primary" onClick={guardar} disabled={saving}><Save size={16} /> {saving ? 'Guardando...' : 'Guardar'}</button>
      </details>

      {cobroNuevo && (
        <div className="modal-overlay" onClick={() => setCobroNuevo(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <h2>Nuevo cobro</h2>
            <form onSubmit={crearCobro}>
              <div className="form-group"><label>Concepto</label><input value={cobroNuevo.concepto} onChange={e => setCobroNuevo({ ...cobroNuevo, concepto: e.target.value })} placeholder="Ej: Abono septiembre" required /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label>Monto</label><InputMonto value={cobroNuevo.monto} onChange={v => setCobroNuevo({ ...cobroNuevo, monto: v })} required /></div>
                <div className="form-group"><label>Vence</label><input type="date" value={cobroNuevo.vencimiento} onChange={e => setCobroNuevo({ ...cobroNuevo, vencimiento: e.target.value })} /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {/* El servicio CONTRATADO por este cliente, no el del catálogo:
                    es lo que hace que el cobro aparezca adentro del servicio en
                    su portal. Sin esto queda suelto y el cliente entra a
                    "Hosting" y no ve un solo pago. */}
                <div className="form-group"><label>¿De qué servicio es?</label>
                  <select value={cobroNuevo.contrato_id || ''} onChange={e => {
                    const ct = d.contratos.find(x => String(x.id) === e.target.value);
                    setCobroNuevo({ ...cobroNuevo, contrato_id: e.target.value, servicio_id: ct?.servicio_id || '' });
                  }}>
                    <option value="">Suelto, no es de ninguno</option>
                    {d.contratos.map(c => <option key={c.id} value={c.id}>{c.nombre || c.servicio}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Estado</label>
                  <select value={cobroNuevo.estado} onChange={e => setCobroNuevo({ ...cobroNuevo, estado: e.target.value })}>
                    <option value="pendiente">Pendiente</option><option value="pagado">Ya pagado</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setCobroNuevo(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Crear</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {nota && (
        <div className="modal-overlay" onClick={() => setNota(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <h2>{nota.id ? 'Editar nota' : 'Nueva nota interna'}</h2>
            <form onSubmit={guardarNota}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
                <div className="form-group"><label>Título</label>
                  <input value={nota.titulo} onChange={e => setNota({ ...nota, titulo: e.target.value })} required autoFocus placeholder="Charla con el dueño" /></div>
                <div className="form-group"><label>Fecha</label>
                  <input type="date" value={nota.fecha} onChange={e => setNota({ ...nota, fecha: e.target.value })} /></div>
              </div>
              <div className="form-group"><label>Nota</label>
                <textarea rows={8} value={nota.detalle || ''} onChange={e => setNota({ ...nota, detalle: e.target.value })}
                  style={{ fontFamily: 'inherit', fontSize: 13 }} placeholder="Lo que quieras recordar de este cliente" /></div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setNota(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {minuta && (
        <div className="modal-overlay" onClick={() => setMinuta(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <h2>{minuta.id ? 'Editar minuta' : 'Cargar minuta'}</h2>
            <form onSubmit={guardarMinuta}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
                <div className="form-group"><label>Título</label>
                  <input value={minuta.titulo} onChange={e => setMinuta({ ...minuta, titulo: e.target.value })} required autoFocus placeholder="Reunión de arranque" /></div>
                <div className="form-group"><label>Fecha</label>
                  <input type="date" value={minuta.fecha} onChange={e => setMinuta({ ...minuta, fecha: e.target.value })} /></div>
              </div>
              <div className="form-group"><label>Minuta</label>
                <textarea rows={12} value={minuta.detalle || ''} onChange={e => setMinuta({ ...minuta, detalle: e.target.value })}
                  style={{ fontFamily: 'inherit', fontSize: 13 }} placeholder="Pegá acá el resumen completo" /></div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={minuta.visible_cliente !== 0} onChange={e => setMinuta({ ...minuta, visible_cliente: e.target.checked ? 1 : 0 })} style={{ width: 16, height: 16 }} />
                Que la vea el cliente en su portal
              </label>
              {minuta.visible_cliente !== 0 && !minuta.id && (
                <SelectorAviso value={minuta.notificar || 'portal'} onChange={v => setMinuta({ ...minuta, notificar: v })} />
              )}
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setMinuta(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {trabajo && (
        <ModalTrabajo cliente_id={id} clienteNombre={d.cliente.nombre}
          onClose={() => setTrabajo(false)} onListo={() => { setTrabajo(false); load(); }} />
      )}

      {contratoNuevo && (
        <div className="modal-overlay" onClick={() => setContratoNuevo(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <h2>{contratoNuevo.id ? 'Editar servicio' : 'Nuevo servicio'}</h2>
            <form onSubmit={crearContrato}>
              <div className="form-group"><label>Concepto</label><input value={contratoNuevo.nombre || ''} onChange={e => setContratoNuevo({ ...contratoNuevo, nombre: e.target.value })} placeholder="Ej: Google Ads" required /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label>Tipo</label>
                  <select value={contratoNuevo.tipo} onChange={e => setContratoNuevo({ ...contratoNuevo, tipo: e.target.value })}>
                    <option value="abono">Abono mensual</option><option value="anual">Renovación anual</option><option value="proyecto">Proyecto</option><option value="unico">Cobro único</option>
                  </select>
                </div>
                <div className="form-group"><label>Monto</label><InputMonto value={contratoNuevo.monto} onChange={v => setContratoNuevo({ ...contratoNuevo, monto: v })} required /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label>{contratoNuevo.tipo === 'anual' ? 'Mes de renovación' : 'Día de cobro'}</label>
                  <input type="number" min="1" max={contratoNuevo.tipo === 'anual' ? 12 : 31}
                    value={contratoNuevo.tipo === 'anual' ? (contratoNuevo.mes_renovacion || '') : (contratoNuevo.dia_cobro || '')}
                    onChange={e => setContratoNuevo({ ...contratoNuevo, [contratoNuevo.tipo === 'anual' ? 'mes_renovacion' : 'dia_cobro']: e.target.value })} />
                </div>
                <div className="form-group"><label>Servicio</label>
                  <select value={contratoNuevo.servicio_id || ''} onChange={e => setContratoNuevo({ ...contratoNuevo, servicio_id: e.target.value })}>
                    <option value="">—</option>{listas.servicios.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                  </select>
                </div>
              </div>
              {contratoNuevo.id && (
                <div className="form-group"><label>Estado</label>
                  <select value={contratoNuevo.estado || 'activo'} onChange={e => setContratoNuevo({ ...contratoNuevo, estado: e.target.value })}>
                    <option value="activo">Activo</option><option value="pausado">Pausado</option><option value="baja">Dado de baja</option>
                  </select>
                </div>
              )}
              {contratoNuevo.id && Number(contratoNuevo.monto) !== Number(d.contratos.find(c => c.id === contratoNuevo.id)?.monto || 0) && (
                <div style={{ fontSize: 12, color: '#f59e0b', marginTop: -6, marginBottom: 12 }}>
                  Cambiaste el monto: se va a registrar como ajuste de precio con fecha de hoy.
                </div>
              )}
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setContratoNuevo(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">{contratoNuevo.id ? 'Guardar' : 'Crear'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {contactoNuevo && (
        <div className="modal-overlay" onClick={() => setContactoNuevo(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <h2>Nuevo usuario del portal</h2>
            <form onSubmit={crearContacto}>
              <div className="form-group"><label>Email *</label><input type="email" value={contactoNuevo.email} onChange={e => setContactoNuevo({ ...contactoNuevo, email: e.target.value })} required autoFocus /></div>
              <div className="form-group"><label>Nombre</label><input value={contactoNuevo.nombre} onChange={e => setContactoNuevo({ ...contactoNuevo, nombre: e.target.value })} /></div>
              {/* El mail se puede dejar para después: crear el acceso, revisarlo
                  y recién ahí dispararlo con el botón del listado. */}
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, fontSize: 13, cursor: 'pointer', margin: '4px 0 10px' }}>
                <input type="checkbox" checked={contactoNuevo.invitar}
                  onChange={e => setContactoNuevo({ ...contactoNuevo, invitar: e.target.checked })}
                  style={{ width: 16, height: 16, marginTop: 2, flex: '0 0 auto' }} />
                <span>
                  Mandarle ahora el mail de bienvenida
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
                    {contactoNuevo.invitar
                      ? 'Le llega el mail con la explicación para entrar.'
                      : 'Se crea el acceso y no se le avisa. Después lo mandás con el botón ✉ del listado.'}
                  </div>
                </span>
              </label>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Eye size={13} /> Que vea sus cobros desde</label>
                <input type="date" value={contactoNuevo.cobros_desde || ''}
                  onChange={e => setContactoNuevo({ ...contactoNuevo, cobros_desde: e.target.value })} />
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 5 }}>
                  Vacío: ve todo su historial. Con una fecha, en el portal solo ve los cobros de ahí en
                  adelante — útil con clientes de hace años. Vos seguís viendo todos acá.
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setContactoNuevo(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Crear</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {factura && (
        <div className="modal-overlay" onClick={() => setFactura(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <h2>Factura del cobro</h2>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '6px 0 14px' }}>{factura.cobro.concepto} · {money(factura.cobro.monto)}</p>
            <form onSubmit={guardarFactura}>
              <div className="grilla-fija" style={{ display: 'grid', gridTemplateColumns: '1fr 80px 1fr', gap: 10 }}>
                <div className="form-group"><label>Número</label><input value={factura.numero || ''} onChange={e => setFactura({ ...factura, numero: e.target.value })} placeholder="0001-00001234" /></div>
                <div className="form-group"><label>Tipo</label>
                  <select value={factura.tipo || ''} onChange={e => setFactura({ ...factura, tipo: e.target.value })}><option value="">—</option><option>A</option><option>B</option><option>C</option></select>
                </div>
                <div className="form-group"><label>Fecha</label><input type="date" value={factura.fecha || ''} onChange={e => setFactura({ ...factura, fecha: e.target.value })} /></div>
              </div>
              <div className="form-group"><label>Archivo</label>
                {factura.url && !factura.archivo && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, padding: '9px 12px',
                    background: 'var(--bg-hover)', borderRadius: 10, fontSize: 13 }}>
                    <FileText size={15} style={{ color: 'var(--accent)', flexShrink: 0 }} />
                    <a href={factura.url} target="_blank" rel="noopener noreferrer" style={{ flex: 1, fontWeight: 600 }}>
                      Abrir la factura cargada
                    </a>
                    {factura.enviada && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>enviada {fecha(factura.enviada)}</span>}
                  </div>
                )}
                <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                  <Upload size={14} /> {factura.archivo
                    ? factura.archivo.name.slice(0, 28)
                    : (factura.url ? 'Reemplazar el archivo' : 'Elegir PDF')}
                  <input type="file" accept="application/pdf,image/*" onChange={e => setFactura({ ...factura, archivo: e.target.files[0] })} style={{ display: 'none' }} /></label>
                {factura.url && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                    Si no elegís un archivo nuevo, se conserva el que ya está cargado.
                  </div>
                )}
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!factura.enviar} onChange={e => setFactura({ ...factura, enviar: e.target.checked })} style={{ width: 16, height: 16 }} />
                <Mail size={14} /> Enviarla por email al cliente
              </label>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setFactura(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {pago && (
        <div className="modal-overlay" onClick={() => setPago(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <h2>Registrar pago</h2>
            <form onSubmit={registrarPago}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label>Fecha</label><input type="date" value={pago.fecha} onChange={e => setPago({ ...pago, fecha: e.target.value })} /></div>
                <div className="form-group"><label>Monto</label><InputMonto value={pago.monto} onChange={v => setPago({ ...pago, monto: v })} /></div>
              </div>
              <div className="form-group"><label>Medio de pago</label>
                <select value={pago.medio_id || ''} onChange={e => setPago({ ...pago, medio_id: e.target.value })}>
                  <option value="">—</option>{listas.medios.map(m => <option key={m.id} value={m.id}>{m.nombre}</option>)}
                </select>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={pago.avisar !== false} onChange={e => setPago({ ...pago, avisar: e.target.checked })} style={{ width: 16, height: 16 }} />
                Avisarle al cliente que recibimos el pago
              </label>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setPago(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Registrar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {accesos && (
        <div className="modal-overlay" onClick={() => setAccesos(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <h2 style={{ marginBottom: 2 }}>Conexiones al portal</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 14 }}>
              {accesos.usuario.nombre || accesos.usuario.email} · últimos 30 días
            </p>
            {accesos.cargando ? <p style={{ color: 'var(--text-muted)' }}>Cargando...</p>
              : accesos.accesos.length ? (
                <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                  {accesos.accesos.map((a, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10,
                      padding: '9px 0', borderTop: i ? '1px solid var(--border)' : 0, fontSize: 13 }}>
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fechaHora(a.cuando)}</span>
                      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                        {/* 'previo' es la última conexión que ya teníamos antes de
                            empezar a guardar el historial: no sabemos cómo entró */}
                        {{ codigo: 'con código', clave: 'con contraseña', previo: 'registro anterior' }[a.via] || ''}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <Empty>
                  No entró en los últimos 30 días.
                  {accesos.usuario.ultimo_acceso && <><br />La última vez fue el {fechaHora(accesos.usuario.ultimo_acceso)}.</>}
                </Empty>
              )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => setAccesos(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {aMudar && (
        <div className="modal-overlay" onClick={() => setAMudar(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <h2>Pasar "{aMudar.proyecto.nombre}" a otro cliente</h2>
            <div className="form-group">
              <label>Nuevo cliente</label>
              <SelectorCliente clientes={clientes.filter(c => String(c.id) !== String(id))}
                clienteId={aMudar.cliente_id} permitirSinCliente={false} permitirNuevo={false}
                onCambio={({ cliente_id }) => setAMudar({ ...aMudar, cliente_id })} />
            </div>
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 18 }}>
              El proyecto se lleva sus cobros y sus servicios: pasan a facturarse al cliente nuevo.
              El historial de movimientos viaja con el proyecto.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="btn btn-secondary" onClick={() => setAMudar(null)}>Cancelar</button>
              <button className="btn btn-primary" disabled={!aMudar.cliente_id} onClick={mudarProyecto}>Pasarlo</button>
            </div>
          </div>
        </div>
      )}

      {aBorrar && (
        <div className="modal-overlay" onClick={() => setABorrar(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <h2>Borrar "{aBorrar.proyecto.nombre}"</h2>

            {aBorrar.cobros.length > 0 ? (
              <>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '4px 0 14px', lineHeight: 1.6 }}>
                  Este proyecto tiene <b>{aBorrar.cobros.length} cobro{aBorrar.cobros.length === 1 ? '' : 's'}</b> por {money(aBorrar.total)}.
                  Si los dejás, van a seguir contando en las cuentas del mes sin proyecto que los explique.
                </p>
                <div style={{ border: '1px solid var(--border)', borderRadius: 10, maxHeight: 200, overflowY: 'auto', marginBottom: 14 }}>
                  {aBorrar.cobros.map(c => (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.concepto}</span>
                      <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{money(c.monto)}</span>
                      {c.pagos > 0
                        ? <Badge label="tiene pagos" color="#22c55e" bg="rgba(34,197,94,0.15)" />
                        : <Badge label={c.estado} color="var(--text-muted)" />}
                    </div>
                  ))}
                </div>
                {aBorrar.con_pagos > 0 && (
                  <p style={{ fontSize: 12.5, color: 'var(--warning)', marginBottom: 14, lineHeight: 1.6 }}>
                    {aBorrar.con_pagos} {aBorrar.con_pagos === 1 ? 'cobro tiene un pago registrado y no se va a borrar' : 'cobros tienen pagos registrados y no se van a borrar'}:
                    esa plata ya entró. Quedan en el cliente, sin proyecto.
                  </p>
                )}
              </>
            ) : (
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '4px 0 14px' }}>
                Este proyecto no tiene cobros asociados.
              </p>
            )}

            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 18, lineHeight: 1.7 }}>
              Se borran además: {[
                aBorrar.movimientos && `${aBorrar.movimientos} movimiento${aBorrar.movimientos === 1 ? '' : 's'}`,
                aBorrar.avances && `${aBorrar.avances} avance${aBorrar.avances === 1 ? '' : 's'}`,
                aBorrar.reuniones && `${aBorrar.reuniones} reunión/es`,
              ].filter(Boolean).join(', ') || 'nada más'}.
              {(aBorrar.servicios > 0 || aBorrar.propuestas > 0 || aBorrar.egresos > 0) && (
                <> Se desvinculan (no se borran): {[
                  aBorrar.servicios && `${aBorrar.servicios} servicio${aBorrar.servicios === 1 ? '' : 's'}`,
                  aBorrar.propuestas && `${aBorrar.propuestas} propuesta${aBorrar.propuestas === 1 ? '' : 's'}`,
                  aBorrar.egresos && `${aBorrar.egresos} egreso${aBorrar.egresos === 1 ? '' : 's'}`,
                ].filter(Boolean).join(', ')}.</>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button className="btn btn-secondary" onClick={() => setABorrar(null)}>Cancelar</button>
              {aBorrar.cobros.length > 0 && (
                <button className="btn btn-secondary" onClick={() => confirmarBorrado('desvincular')}>
                  Borrar y dejar los cobros
                </button>
              )}
              <button className="btn btn-danger" onClick={() => confirmarBorrado(aBorrar.cobros.length ? 'borrar' : 'desvincular')}>
                {aBorrar.cobros.length ? 'Borrar todo' : 'Borrar proyecto'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
